import { Breadcrumb, Button, Flex, Row, Space } from "antd";
import { useTranslation } from "react-i18next";
import { ReactComponent as DownloadIcon } from "../../../assets/icon/downloadIcon.svg";
import { useEffect, useRef, useState } from "react";
import styles from "../styles/MyTeamSubordinate.module.css";
import { toJpeg } from "html-to-image";
import { useSelector } from "react-redux";
import { OrgChart } from "d3-org-chart";
import * as d3 from "d3";

const MyTeamSubordinate = ({ graphRef = null }) => {
    const { t, i18n } = useTranslation();
    const chartRef = useRef(null);
    const [chart, setChart] = useState(null);
    const subOrdinateList = useSelector((state) => state.myteam.subOrdinatesList);
    const supervisor = useSelector((state) => state.myteam.supervisorDetails);
    const [isChartDownloading, setIsChartDownloading] = useState(false);

    const downloadImage = async () => {
        const chartElement = document.getElementById('employeeEmploymentSubordinates');

        if (!chartElement) {
            console.error('Element with id "employeeEmploymentSubordinates" not found');
            return;
        }

        const originalWidth = chartElement.style.width;
        const originalHeight = chartElement.style.height;

        chartElement.style.width = `${chartElement.scrollWidth}px`;
        chartElement.style.height = `${chartElement.scrollHeight}px`;

        const scale = 10;
        try {
            const dataUrl = await toJpeg(chartElement, {
                width: chartElement.scrollWidth * scale,
                height: chartElement.scrollHeight * scale,
                backgroundColor: "white",
                style: {
                    transform: `scale(${scale})`,
                    transformOrigin: 'top left',
                    width: `${chartElement.scrollWidth}px`,
                    height: `${chartElement.scrollHeight}px`,
                },
            });

            chartElement.style.width = originalWidth;
            chartElement.style.height = originalHeight;

            const link = document.createElement('a');
            link.href = dataUrl;
            link.download = 'organizational-chart.jpg';
            link.click();

        } catch (error) {
            console.error('Could not generate image', error);
        }
    };

    const handleDownloadClick = async () => {
        try {
            setIsChartDownloading(true);
            let res = await chart?.compact?.(false).render().fit().render();
            if (res) {
                await new Promise(resolve => setTimeout(resolve, 3000));
                downloadImage();
            }
        } catch (error) {
            console.error('Error during download:', error);
        } finally {
            setIsChartDownloading(false);
        }
    };

    function transformData(data, lang) {
        const result = [
            {
                id: supervisor.empId,
                preventExpand: true
                , parentId: null,
                name: supervisor?.['name' + (lang == 'en' ? "En" : "")] + " " + supervisor?.['surName' + (lang == 'en' ? "En" : "")],
                position: supervisor?.['positionName' + (lang == 'en' ? "En" : "")] || "",
            }];
        function traverse(node, parentId = null) {
            console.log("node", node);

            const transformedNode = {
                id: node.empId || node.id,
                parentId: node.supervisorId || 'root',
                name: node?.['name' + (lang == 'en' ? "En" : "")] + " " + node?.['surName' + (lang == 'en' ? "En" : "")] || node.name,
                position: node?.['positionName' + (lang == 'en' ? "En" : "")] || "",
                image: node.image || '',
                email: node.email || '',
                phone_number: node.phone_number || '',
                hire_date: node.hire_date || '',
                preventExpand: true
            };

            result.push(transformedNode);

            if (node.children && node.children.length > 0) {
                node.children.forEach(child => traverse(child, transformedNode.id));
            }
        }
        data.map(o => traverse(o))
        return result;
    }

    useEffect(() => {
        const chartContainer = chartRef?.current;

        if (!chartContainer) {
            console.error("Chart container is not available.");
            return;
        }
        const orgChart = new OrgChart()
            .nodeHeight((d) => 50 + 25)
            .nodeWidth((d) => 300 + 2)
            .childrenMargin((d) => 150)
            .compactMarginBetween((d) => 200)
            .compactMarginPair((d) => 200)
            .neighbourMargin((a, b) => 200)

            .buttonContent(() => { })
            .onNodeClick((d) => console.log(d))
            .nodeContent(function (d) {
                const color = '#FFFFFF';
                const imageDiffVert = 25 + 2;
                return `
                <div id='node-na' style="width:${d.width}px;height:${d.height}px;padding-left:1px;padding-right:1px;">
                  <div style="background-color:${color};margin-left:-1px;width:${d.width - 2}px;border-radius:100px;border: 1px solid black ;padding-left :20px;padding-right :20px;">
                    <div style="min-height:${d.height - 20}px;align-content:center;font-size:1.3vw;color:#08011E;text-align:center ;white-space: normal; word-wrap: break-word; overflow: hidden; text-overflow: ellipsis;">${d.data?.name}</div>
                <div style="margin-bottom:15px;align-content:center;font-size:1vw;color:#08011E;text-align:center">${"(" + d.data?.position + ")"}</div>
                  </div>
                </div>`;
            })
            .container(chartContainer)
            .data(transformData(subOrdinateList, i18n.language))
            .render();

        const svg = d3.select(chartContainer).select("svg");

        if (svg) {
            svg.selectAll(".node")
                .style("cursor", "default");
            svg.selectAll("foreignObject").remove()
        }
        orgChart?.expandAll()
        orgChart?.compact?.(false).render();
        orgChart?.nodeButtonWidth((node) => node.data?.preventExpand ? 0 : 20)
            .nodeButtonHeight((node) => node.data?.preventExpand ? 0 : 20);
        const rootNodeId = subOrdinateList[0]?.empId;
        if (rootNodeId) {
            orgChart.setCentered(rootNodeId);
        }

        const adjustChartSize = () => {
            orgChart.fit();
        };
        setChart(orgChart);
        return () => {
            if (chart) {
                chart?.destroy?.();
            }
        };
    }, [subOrdinateList, supervisor, i18n.language]);

    useEffect(() => {
        if (graphRef?.current) {
            graphRef.current = chart
        }
    }, [chart])
    return (
        <Space direction="vertical" size={24} style={{ width: "100%" }}>
            <Flex vertical>
                <Breadcrumb items={[
                    { title: t("home.header"), href: "/" },
                    { title: t("my_team.header"), href: "" },
                    { title: t("subordinate.header") },
                ]} />
                <h2>{t("subordinate.header")}</h2>
            </Flex>

            <Row justify="end">
                <Button type="default"
                    disabled={isChartDownloading}
                    onClick={handleDownloadClick}
                    style={{ padding: "8px 16px", height: "44px", borderRadius: "8px", display: "flex", alignItems: "center" }}
                    loading={isChartDownloading}
                    icon={
                        !isChartDownloading ? (
                            <DownloadIcon style={{ fontSize: 16 }} />
                        ) : (
                            <span style={{ width: 16, height: 16 }} />
                        )
                    }
                >
                    <span style={{ color: "#667085", fontWeight: "bold" }}>{t('common.download')}</span>
                </Button>
            </Row>

            <div>
                <div className={styles.stylesNode2}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px', gap: "12px" }}>
                        <Button onClick={() => {
                            chart.nodeHeight(() => 125 + 25);
                            chart.layout('left').render().fit()
                        }}>
                            {t("og_chart.left")}
                        </Button>
                        <Button onClick={() => {
                            chart.nodeHeight(() => 50 + 25);
                            chart.layout('top').render().fit()
                        }}>
                            {t("og_chart.top")}
                        </Button>
                    </div>
                    <div id="employeeEmploymentSubordinates" style={{ width: "100%" }} ref={chartRef} className="chart-container"></div>
                </div>
            </div>
        </Space>
    )
}

export default MyTeamSubordinate;